import * as Color from "color"
import PptxGenJS, {
	ChartMultiTypeItem,
	ChartMultiTypeItemOptions,
	ChartOptions,
	Slide,
	ChartMultiTypeOptions,
	ChartBarDataset
} from "pptxgenjs"
import {
	createGlobalChartData,
	createTaxesChartData,
	createPerInhabitantChartData,
	getProspectiveTaxes
} from "./utils"
import { ReportConfiguration } from "../../../configuration"
import { getChartBaseOptions, getChartColors } from "../../pptx-configuration/charts"
import * as Constants from "../../pptx-configuration/constants"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../../pptx-configuration/placeholders"
import { DocumentSection } from "../../types"
import { formatTitle, createDemographyData } from "../../utils"
import * as TaxDomain from "../../../../../domain/sfp/tax-domain"
import { LocalAuthority } from "../../../../../model"
import { ColorsHelper } from "../../../../../util/colors"
import * as Model from "../../../../../model"

export function checkTaxSection(configuration: ReportConfiguration): boolean {
	const charts = configuration.observation.taxCharts
	return charts.global || !!(charts.taxes && charts.taxes.length)
}

export function createTaxSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	localAuthority: LocalAuthority,
	years: number[],
	scenario: Model.Scenario
): DocumentSection {
	const title = formatTitle("Fiscalité", numbering)
	const slides: Slide[] = []
	const referenceYear = configuration.generalities.referenceYear
	const placeholderBodyOptions = getPlaceholderChartBodyOptions()
	const baseColor = new Color(configuration.table.header.backgroundColor)

	const prospectiveTaxes = getProspectiveTaxes(scenario,referenceYear)

	if (configuration.observation.taxCharts.global) {
		const chartTitle = `Année ${referenceYear}`
		const data = createGlobalChartData(
			chartTitle,
			localAuthority.observations,
			referenceYear
		)

		const slide = pptx.addNewSlide(MasterSlide.Chart)
		slide.addText(title, {
			placeholder: Placeholder.Title
		})

		const options: ChartOptions = {
			...getChartBaseOptions(placeholderBodyOptions),
			title: chartTitle,
			chartColors:getChartColors(),
			showPercent:true,
			dataLabelFontBold:true,
			legendFontSize:11,
		}

		slide.addChart(pptx.charts.PIE, data, options)

		slides.push(slide)
	}

	const taxes = configuration.observation.taxCharts.taxes

	if (taxes && taxes.length) {
		let taxesSlide = pptx.addNewSlide(MasterSlide.Chart)
		taxesSlide.addText(title, {
			placeholder: Placeholder.Title
		})

		const taxesChartData = createTaxesChartData(
			taxes,
			localAuthority.observations,
			years,
			prospectiveTaxes,
			localAuthority.accounts,
			referenceYear
		)

		const taxesChartOptions: ChartOptions = {
			...getChartBaseOptions(placeholderBodyOptions),
			showTitle: false,
			chartColors: getChartColors(),
			legendFontSize:11,
		}

		taxesSlide.addChart(pptx.charts.BAR, taxesChartData, taxesChartOptions)

		if (configuration.observation.taxCharts.onePerTax) {
			taxes.forEach((tax, taxIndex) => {
				const caption = `Par taxe : ${TaxDomain.getTaxLabel(tax)}`
				const perTaxChartData: [ChartBarDataset] = [{
					labels: taxesChartData[taxIndex].labels.slice(),
					name: taxesChartData[taxIndex].name,
					values: taxesChartData[taxIndex].values.slice()
				}]

				const perTaxSlide = pptx.addNewSlide(MasterSlide.Chart)

				perTaxSlide.addText(title, {
					placeholder: Placeholder.Title
				})

				const perTaxChartOptions: ChartOptions = {
					...getChartBaseOptions(placeholderBodyOptions),
					showLegend: false,
					title: caption,
					chartColors: [baseColor.hex().substr(1)],
					showValue:true,
				}

				perTaxSlide.addChart(pptx.charts.BAR, perTaxChartData, perTaxChartOptions)
			})
		}

		const perInhabitantSlide = pptx.addNewSlide(MasterSlide.Chart)
		perInhabitantSlide.addText(title, {
			placeholder: Placeholder.Title
		})

		const perInhabitantData = createPerInhabitantChartData(
			taxesChartData,
			localAuthority.demographies,
			referenceYear
		)

		const populationData = createDemographyData(
			localAuthority.demographies,
			years,
			referenceYear
		)

		const populationChartOptions: ChartMultiTypeItemOptions = {
			chartColors: ["666666"],
			chartColorsOpacity: 50,
			lineSize: 1,
			secondaryCatAxis: true,
			secondaryValAxis: true
		}

		const perInhabitantChartOptions: ChartMultiTypeOptions = {
			...getChartBaseOptions(placeholderBodyOptions, null),
			title: "Par habitant",
			catAxes: [{}, { catAxisHidden: true }],
			valAxes: [
				{ valAxisLabelFormatCode: Constants.STRING_FORMAT_CURRENCY },
				{
					valAxisLabelFormatCode: "#,##0",
					valAxisLineShow: true,
					valGridLine: "none"
				}
			],
			chartColors: getChartColors(),
			legendFontSize:11,
		} as ChartMultiTypeOptions

		const perInhabitantCharts: ChartMultiTypeItem[] = [
			{ data: perInhabitantData, type: pptx.charts.BAR },
			{
				data: populationData,
				options: populationChartOptions,
				type: pptx.charts.LINE
			}
		]

		perInhabitantSlide.addChart(perInhabitantCharts, perInhabitantChartOptions)

		if (configuration.observation.taxCharts.onePerTaxPerInhabitant) {
			taxes.forEach((tax, taxIndex) => {
				const perTaxPerInhabitantSlide = pptx.addNewSlide(MasterSlide.Chart)
				perTaxPerInhabitantSlide.addText(title, {
					placeholder: Placeholder.Title
				})

				const caption = `Par taxe et par habitant : ${TaxDomain.getTaxLabel(tax)}`
				const perTaxPerInhabitantData: [ChartBarDataset] = [{
					labels: perInhabitantData[taxIndex].labels.slice(),
					name: perInhabitantData[taxIndex].name,
					values: perInhabitantData[taxIndex].values.slice()
				}]

				const bodyOptions = getPlaceholderChartBodyOptions()
				const perTaxPerInhabitantChartOptions = {
					...getChartBaseOptions(bodyOptions),
					showLegend: false,
					title: caption,
					chartColors: [baseColor.hex().substr(1)],
					showValue:true
				}

				perTaxPerInhabitantSlide.addChart(
					pptx.charts.BAR,
					perTaxPerInhabitantData,
					perTaxPerInhabitantChartOptions
				)
			})
		}
	}

	return { slides, title }
}


// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/tax-generator/index.ts